/*
 * <recommended-products-section> — behavior for sections/section-recommended-products.liquid.
 *
 * For the "Shopify recommendations" source, `recommendations.performed` is only ever
 * true when this section is rendered through routes.product_recommendations_url - a normal
 * page load never populates it. So the initial render ships an empty [data-recommended-content]
 * plus a [data-recommendations-url], and this fetches that same section from the recommendations
 * endpoint and swaps its content in - same approach the theme already uses for
 * <complementary-products> in assets/theme.js.
 *
 * Slide movement itself is native CSS scroll-snap (see section-recommended-products.css);
 * the pagination-dots logic here only builds/syncs dots for whichever slide is in view.
 */
(function () {
  if (customElements.get('recommended-products-section')) return;

  customElements.define(
    'recommended-products-section',
    class RecommendedProductsSection extends HTMLElement {
      connectedCallback() {
        const url = this.getAttribute('data-recommendations-url');
        const content = this.querySelector('[data-recommended-content]');

        if (url && content && !content.querySelector('[data-recommended-slider]')) {
          this._observer = new IntersectionObserver(
            (entries) => {
              if (!entries[0].isIntersecting) return;
              this._observer.disconnect();
              this._fetchRecommendations(url, content);
            },
            { rootMargin: '0px 0px 400px 0px' }
          );
          this._observer.observe(this);
          return;
        }

        this._initPagination();
      }

      _fetchRecommendations(url, content) {
        fetch(url)
          .then((response) => response.text())
          .then((text) => {
            const html = document.createElement('div');
            html.innerHTML = text;
            const fetchedContent = html.querySelector('[data-recommended-content]');
            if (fetchedContent) content.innerHTML = fetchedContent.innerHTML;

            if (!content.querySelector('[data-recommended-slider]')) {
              this.hidden = true;
              return;
            }

            this._initPagination();
          })
          .catch((e) => console.error(e));
      }

      _initPagination() {
        const track = this.querySelector('[data-recommended-track]');
        const pagination = this.querySelector('[data-recommended-pagination]');
        const slides = this.querySelectorAll('[data-recommended-slide]');
        if (!track || !pagination || slides.length < 2) return;

        const dots = Array.from(slides).map((slide, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'recommended-products__pagination-dot';
          dot.setAttribute('aria-label', `${index + 1} / ${slides.length}`);
          dot.addEventListener('click', () => {
            slide.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
          });
          pagination.appendChild(dot);
          return dot;
        });
        dots[0].classList.add('is-active');

        this._sliderObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const index = Array.from(slides).indexOf(entry.target);
              dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
            });
          },
          { root: track, threshold: 0.6 }
        );
        slides.forEach((slide) => this._sliderObserver.observe(slide));
      }

      disconnectedCallback() {
        this._observer?.disconnect();
        this._sliderObserver?.disconnect();
      }
    }
  );
})();
